/**
 * Adding a client.
 *
 * The three files are the whole point of the form; the text fields exist to describe them. The
 * service asks the Python CLI whether the result is acceptable, so the messages shown here are
 * the same ones a command-line build would print.
 */
import { useState } from 'react';
import { ApiError, api, type KeystoreResult } from '../api';
import { IconFit, ICON_SCALE_DEFAULT } from './IconFit';
import { KeystoreChoiceFields, KeystoreSummary, type KeystoreMode } from './KeystoreChoice';

function deriveSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

export function NewClient({ onCreated, onCancel }: { onCreated: (slug: string) => void; onCancel: () => void }) {
  const [appName, setAppName] = useState('');
  const [slug, setSlug] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [keystoreMode, setKeystoreMode] = useState<KeystoreMode>('generate');
  const [keystore, setKeystore] = useState<KeystoreResult | null>(null);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [tab, setTab] = useState<'shared' | 'android' | 'ios'>('shared');
  const [logo, setLogo] = useState<File | null>(null);
  const [iconBackground, setIconBackground] = useState('');
  const [iconScale, setIconScale] = useState(ICON_SCALE_DEFAULT);
  const [iconTrim, setIconTrim] = useState(false);

  const effectiveSlug = slug || deriveSlug(appName);

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSaving(true);
    const form = new FormData(event.currentTarget);
    if (!form.get('slug')) form.set('slug', effectiveSlug);

    // The keystore and its passwords belong to the keystore endpoint, which stores them
    // encrypted. Sending them to /api/clients as well would upload the file twice and put
    // signing passwords through a request that has no use for them.
    const KEYSTORE_FIELDS = ['keystore', 'keyAlias', 'storePassword', 'keyPassword'] as const;
    const keyForm = new FormData();
    for (const field of KEYSTORE_FIELDS) {
      const value = form.get(field);
      if (value !== null) keyForm.set(field, value);
      form.delete(field);
    }

    try {
      const created = await api.createClient(form);
      // The client exists now; the key is a second step so a keystore problem cannot lose the
      // rest of the form. Failures here are reported without discarding the client.
      if (keystoreMode === 'generate') {
        setCreatedSlug(created.slug);
        setKeystore(await api.generateKeystore(created.slug));
        return;
      }
      if (keystoreMode === 'upload') {
        setCreatedSlug(created.slug);
        setKeystore(await api.uploadKeystore(created.slug, keyForm));
        return;
      }
      onCreated(created.slug);
    } catch (error) {
      const failure = error as ApiError;
      setErrors(failure.errors?.length ? failure.errors : [failure.message]);
    } finally {
      setSaving(false);
    }
  }

  // The client is already saved by this point; the fingerprint is the last thing worth showing.
  if (keystore && createdSlug) {
    return (
      <div className="inner">
        <header className="run-head">
          <div className="run-title"><h1>{createdSlug} added</h1></div>
        </header>
        <KeystoreSummary result={keystore} onDone={() => onCreated(createdSlug)} />
      </div>
    );
  }

  return (
    <div className="inner">
      <header className="run-head">
        <div className="run-title"><h1>Add a client</h1></div>
        <p style={{ color: 'var(--ink-2)', margin: '10px 0 0', maxWidth: '60ch' }}>
          The logo becomes every launcher icon and the Play Store icon. The Firebase file must already
          list this package name, or the build will stop before Gradle starts.
        </p>
      </header>

      <form className="form section" onSubmit={submit}>
        {errors.length > 0 && (
          <div className="errors">
            {errors.map((message) => <span key={message}>{message}</span>)}
          </div>
        )}

        <div className="tabs" role="tablist">
          {([['shared', 'The app'], ['android', 'Android'], ['ios', 'iOS']] as const).map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key}
                    onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
          <span className="tab-note">One form; the tabs only decide what is on screen.</span>
        </div>

        <div className="tab-panel" hidden={tab !== 'shared'}>
        <div className="field">
          <label htmlFor="appName">App name</label>
          <input id="appName" name="appName" type="text" required value={appName}
                 onChange={(event) => setAppName(event.target.value)} placeholder="Radio Example" />
          <span className="hint">Shown under the icon on the phone.</span>
        </div>

        <div className="pair">
          <div className="field">
            <label htmlFor="slug">Folder name</label>
            <input id="slug" name="slug" type="text" value={slug}
                   onChange={(event) => setSlug(event.target.value)} placeholder={effectiveSlug || 'radio-example'} />
            <span className="hint">Defaults to {effectiveSlug || 'a name derived from the app name'}.</span>
          </div>
          <div className="field">
            <label htmlFor="appId">App ID</label>
            <input id="appId" name="appId" type="number" min={0} required placeholder="123" />
          </div>
        </div>

        <div className="field">
          <label htmlFor="appDomain">Backend URL</label>
          <input id="appDomain" name="appDomain" type="text" required
                 placeholder="https://www.apps-admin2.com/radio/example" />
        </div>

        <div className="pair">
          <div className="field">
            <label htmlFor="versionName">Version name</label>
            <input id="versionName" name="versionName" type="text" placeholder="Leave blank to follow the template" />
          </div>
          <div className="field">
            <label htmlFor="lastVersionCode">Last version code on Play</label>
            <input id="lastVersionCode" name="lastVersionCode" type="number" min={0} defaultValue={0} />
            <span className="hint">The next build uses this plus one. Zero for a brand-new app.</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="aboutMenuTitle">About page menu title (optional)</label>
          <input id="aboutMenuTitle" name="aboutMenuTitle" type="text"
                 placeholder="About Us" />
          <span className="hint">
            Only needed if this client names it something the smoke test will not recognise. It
            already finds titles containing &ldquo;About&rdquo; or &ldquo;Σχετικ&rdquo; on its own.
          </span>
        </div>

        <div className="field">
          <label htmlFor="iconBackground">Icon background (optional)</label>
          <input id="iconBackground" name="iconBackground" type="text" placeholder="#FFFFFF"
                 value={iconBackground} onChange={(event) => setIconBackground(event.target.value)} />
          <span className="hint">Shows behind the logo on the launcher icon. White if left blank.</span>
        </div>

        <div className="field">
          <label htmlFor="logo">Logo</label>
          <input id="logo" name="logo" type="file" accept="image/png" required
                 onChange={(event) => setLogo(event.target.files?.[0] ?? null)} />
          <span className="hint">Square PNG, 1024 px.</span>
        </div>

        <div className="field">
          <label>How it lands on the phone</label>
          <IconFit
            logo={logo}
            scale={iconScale}
            trim={iconTrim}
            background={iconBackground}
            onScale={setIconScale}
            onTrim={setIconTrim}
          />
          <input type="hidden" name="iconScale" value={iconScale} />
          <input type="hidden" name="iconTrim" value={String(iconTrim)} />
        </div>
        </div>

        <div className="tab-panel" hidden={tab !== 'android'}>
          <div className="field">
            <label htmlFor="packageName">Package name</label>
            <input id="packageName" name="packageName" type="text" required
                   placeholder="gr.app.radioexample" />
            <span className="hint">
              Permanent once the app is on Play. Capitals are allowed and are kept as typed,
              because Play matches it exactly.
            </span>
          </div>

          <div className="field">
            <label htmlFor="googleServices">google-services.json</label>
            <input id="googleServices" name="googleServices" type="file"
                   accept="application/json,.json" required />
            <span className="hint">From Firebase, for the package name above.</span>
          </div>

          <KeystoreChoiceFields mode={keystoreMode} onMode={setKeystoreMode} slug={effectiveSlug || 'new'} />
        </div>

        <div className="tab-panel" hidden={tab !== 'ios'}>
          <div className="field">
            <label htmlFor="bundleId">Bundle identifier</label>
            <input id="bundleId" name="bundleId" type="text" placeholder="the package name" />
            <span className="hint">
              Leave blank if it matches Android. It is a separate listing in a separate store, so
              it often does not.
            </span>
          </div>

          <div className="field">
            <label htmlFor="googleServiceInfo">GoogleService-Info.plist</label>
            <input id="googleServiceInfo" name="googleServiceInfo" type="file"
                   accept=".plist,application/xml" />
            <span className="hint">
              From Firebase, for the bundle identifier. Required before an iOS build will start:
              without it the app would ship the template&rsquo;s own Firebase project.
            </span>
          </div>

          <p className="hint" style={{ maxWidth: '62ch' }}>
            The signing certificate is added after the client exists, under{' '}
            <strong>Set up iOS signing</strong>. Without it iOS builds still produce screenshots,
            just no .ipa.
          </p>
        </div>

        <div className="actions">
          <button className="btn" type="submit" disabled={saving}>
            {saving ? 'Saving' : 'Add client'}
          </button>
          <button className="btn quiet" type="button" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
